import { createContext, useContext, useState, useEffect } from "react";

// Countries: which languages each one offers, which is the default
// when that country is first selected, and — new — the real subdomain
// that country actually lives on. "available: false" entries show as
// Coming Soon and can't actually be selected.
export const COUNTRIES = {
  global: { label: "Global (HQ)", flag: "🌐", available: true, languages: ["en"], defaultLanguage: "en", hostname: "tapaswe.net" },
  india: { label: "Bharat (India)", flag: "🇮🇳", available: true, languages: ["hi", "en"], defaultLanguage: "hi", hostname: "bha.tapaswe.net" },
  usa: { label: "USA", flag: "🇺🇸", available: true, languages: ["en"], defaultLanguage: "en", hostname: "usa.tapaswe.net" },
  russia: { label: "Russia", flag: "🇷🇺", available: false, languages: ["en"], defaultLanguage: "en", hostname: null },
  australia: { label: "Australia", flag: "🇦🇺", available: false, languages: ["en"], defaultLanguage: "en", hostname: null },
};

export const LANGUAGES = {
  en: { label: "English" },
  hi: { label: "हिन्दी" },
};

// hostname → country code, built from the map above. "www.tapaswe.net"
// isn't its own entry — it's just an alias for the bare domain, both
// mean "global".
const HOSTNAME_TO_COUNTRY = { "www.tapaswe.net": "global" };
Object.entries(COUNTRIES).forEach(([code, c]) => {
  if (c.hostname) HOSTNAME_TO_COUNTRY[c.hostname] = code;
});

// One-time, best-effort IP geolocation redirect — only fires on a
// first-ever visit to the bare global domain, with no stored
// preference yet. Never blocks rendering, never retries once it's
// run once (successful or not), and fails silently to "stay on
// global" if the API is slow, blocked, or returns something we don't
// recognize. Undefined/unrecognized locations simply stay on global,
// exactly as asked.
async function tryGeoRedirect() {
  const alreadyDecided = localStorage.getItem("tapaswe-geo-redirect-done");
  const onGlobalHost = window.location.hostname === "tapaswe.net" || window.location.hostname === "www.tapaswe.net";
  if (alreadyDecided || !onGlobalHost) return;

  localStorage.setItem("tapaswe-geo-redirect-done", "1"); // set first — never re-attempt, even on failure below

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
    clearTimeout(timeout);
    const data = await res.json();

    const target =
      data.country_code === "IN" ? COUNTRIES.india.hostname :
      data.country_code === "US" ? COUNTRIES.usa.hostname :
      null; // any other country: stay on global, exactly as specified

    if (target) {
      window.location.href = `https://${target}${window.location.pathname}`;
    }
  } catch {
    // Geolocation lookup failed or timed out — stay on global. Never
    // let this break or delay the actual site.
  }
}

const LocaleContext = createContext(null);

export const LocaleProvider = ({ children }) => {
  const [country, setCountryState] = useState(() => {
    const fromHostname = HOSTNAME_TO_COUNTRY[window.location.hostname];
    if (fromHostname) return fromHostname; // the domain itself is the source of truth
    return localStorage.getItem("tapaswe-country") || "global";
  });
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem("tapaswe-language");
    const defaultLang = COUNTRIES[country]?.defaultLanguage || "en";
    const validForCountry = COUNTRIES[country]?.languages || ["en"];
    // Guards against a stale value from a language that's since been
    // removed (e.g. Spanish) or that isn't valid for this country —
    // without this, a returning visitor's old localStorage value
    // could reference a language that no longer exists.
    if (stored && LANGUAGES[stored] && validForCountry.includes(stored)) {
      return stored;
    }
    return defaultLang;
  });

  useEffect(() => {
    localStorage.setItem("tapaswe-country", country);
  }, [country]);

  useEffect(() => {
    localStorage.setItem("tapaswe-language", language);
  }, [language]);

  useEffect(() => {
    tryGeoRedirect();
  }, []);

  // Changing country now means navigating to that country's real
  // subdomain (a full page load), not just updating in-page state —
  // these are meant to be actual different sites. The new page's
  // LocaleProvider then picks up the country from its own hostname.
  const setCountry = (code) => {
    const entry = COUNTRIES[code];
    if (!entry || !entry.available) return; // guard: Coming Soon countries aren't selectable

    if (entry.hostname && entry.hostname !== window.location.hostname) {
      window.location.href = `https://${entry.hostname}${window.location.pathname}`;
      return;
    }
    // Already on the right host (or no distinct host) — just update state.
    setCountryState(code);
    setLanguageState(entry.defaultLanguage);
  };

  // Changing language is independent of country — selecting a language
  // afterward does NOT change the selected country back. Only languages
  // the current country actually offers are selectable (e.g. USA can't
  // pick Hindi) — the UI only ever shows valid options anyway, this is
  // just a second guard against stale/invalid state.
  const setLanguage = (code) => {
    const available = COUNTRIES[country]?.languages || [];
    if (!LANGUAGES[code] || !available.includes(code)) return;
    setLanguageState(code);
  };

  const availableLanguages = COUNTRIES[country]?.languages || ["en"];

  return (
    <LocaleContext.Provider value={{ country, language, setCountry, setLanguage, availableLanguages }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => useContext(LocaleContext);
